import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spinner, confirmAction } from '@/components/ui';
import type { Form, FormField } from '../../../services/formService';
import { updateHiredEquipmentLines } from '../../../services/hiredEquipmentService';
import type { Task } from '../../../types/Task';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canApproveSubmissionForForm,
  canEditDailyLogForForm,
} from '../../forms/utils/submissionPermissions';
import { useHiredEquipmentGridData } from '../hooks/useHiredEquipmentGridData';
import { useHeLinePricing } from '../hooks/useHeLinePricing';
import { usePoBalanceWarning } from '../hooks/usePoBalanceWarning';
import type { HiredEquipmentGridRow } from '../types';
import {
  heAnchorFromForm,
  heGridPersistPayload,
  heGridSubmitRows,
  parseHeGridRows,
  withSavedLineComments,
} from '../utils/heSummaryRows';
import { validateGridRows } from '../utils/gridValidation';
import {
  appendMissingPoResources,
  duplicateRow,
  emptyRowFromAnchor,
  lumpSumRowFromAnchor,
  resolveLockedPoId,
} from '../utils/poPrefill';
import { defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import FormPictures from '../../forms/components/FormPictures';
import HiredEquipmentGrid from './HiredEquipmentGrid';
import { formatHeServiceAreaLabel, heWorkAreaLabels } from '../utils/approvalListRow';
import HiredEquipmentTaskPictures from './HiredEquipmentTaskPictures';
import PoBalanceWarningModal from './PoBalanceWarningModal';
import type { HiredEquipmentAnchor } from '../types';
import { getHiredEquipmentInternalComment } from '../utils/internalComment';
import { endSentence } from '../../../utils/uiText';
import HiredEquipmentInternalComment from './HiredEquipmentInternalComment';

function loggedEditKey(rows: HiredEquipmentGridRow[], internalComment: string): string {
  const complete = rows.filter(
    (row) => row.taskId != null && row.qty != null && row.rate != null && Boolean(row.date),
  );
  return JSON.stringify({
    rows: heGridSubmitRows(complete),
    internalComment,
  });
}

/** Every line, complete or not, plus the comment: what "unsaved changes" compares (V2-034). */
function draftKey(rows: HiredEquipmentGridRow[], internalComment: string): string {
  return JSON.stringify({ rows: heGridPersistPayload(rows), internalComment });
}

interface HiredEquipmentDetailedViewProps {
  form: Form;
  availableTasks: Task[];
  onFieldsReplace?: (formId: string, fields: FormField[]) => void;
  onResubmitted?: (formId: string) => void | Promise<void>;
}

const HiredEquipmentDetailedView: React.FC<HiredEquipmentDetailedViewProps> = ({
  form,
  availableTasks,
  onFieldsReplace,
  onResubmitted,
}) => {
  const { hasPermission } = usePermissions();
  const status = (form.status || '').toLowerCase();
  const needsRevisions = status === 'need revisions';
  const statusAllowsEdit =
    status === 'pending' || status === 'revisions completed' || needsRevisions;
  const { currentUser } = useAuth();
  // V2-002: while it waits for approval, a submission is read-only for its submitter (the
  // server refuses the edit too). They edit again once it is sent back for revisions.
  const isOwnSubmission = Boolean(
    currentUser && form.statusRequestedBy?.id != null && form.statusRequestedBy.id === currentUser.id,
  );
  const canEdit =
    statusAllowsEdit &&
    (needsRevisions || !isOwnSubmission) &&
    (canEditDailyLogForForm(hasPermission, form) || canApproveSubmissionForForm(hasPermission, form));

  const baseAnchor = useMemo(() => heAnchorFromForm(form.fields), [form.fields]);
  const [rows, setRows] = useState<HiredEquipmentGridRow[]>(() => parseHeGridRows(form.fields, availableTasks));
  const [internalComment, setInternalComment] = useState(() =>
    getHiredEquipmentInternalComment(form.fields),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [hasResubmitted, setHasResubmitted] = useState(false);
  // V2-034 / EDIT-05: explicit save on a submitted record. Nothing is written while typing; the
  // user chooses Save Changes, Cancel / Discard Changes, or Resubmit (which saves too).
  const [editing, setEditing] = useState(false);
  /** The last saved fields – what Cancel / Discard Changes goes back to. */
  const savedFieldsRef = useRef<FormField[]>(form.fields);
  /** Lines and comment as last saved, for "unsaved changes". */
  const savedDraftKeyRef = useRef(
    draftKey(parseHeGridRows(form.fields, availableTasks), getHiredEquipmentInternalComment(form.fields)),
  );
  /** The last logged edit, for the one History entry a save writes. */
  const historyBaselineRef = useRef(
    loggedEditKey(parseHeGridRows(form.fields, availableTasks), getHiredEquipmentInternalComment(form.fields)),
  );
  const commentRef = useRef(internalComment);
  commentRef.current = internalComment;
  const waitingOnRevisions = needsRevisions && !hasResubmitted;
  /** Revisions are always being edited; an approver opens editing with Edit Lines. */
  const isEditing = canEdit && (waitingOnRevisions || editing);
  const isDirty = isEditing && draftKey(rows, internalComment) !== savedDraftKeyRef.current;

  const lookups = useHiredEquipmentGridData(baseAnchor, rows);
  const poBalanceWarning = usePoBalanceWarning();
  // C4 / M6 while editing: re-price each line at the rate on its date (the save then stores it, and
  // approval re-checks it on the server). Read-only viewers see the stored amounts.
  const pricing = useHeLinePricing(rows, setRows, {
    serviceAreaId: baseAnchor.serviceAreaId,
    excludeFormId: form.id,
    enabled: canEdit,
    // H4: the approver sees the same compliance warnings as the person who entered the lines.
    checkCompliance: true,
  });

  const anchor = useMemo((): HiredEquipmentAnchor => {
    const lockedPoId = resolveLockedPoId(baseAnchor, rows, lookups.purchaseOrders);
    if (lockedPoId == null) return baseAnchor;
    return { ...baseAnchor, lockedPoId };
  }, [baseAnchor, rows, lookups.purchaseOrders]);

  useEffect(() => {
    setRows(parseHeGridRows(form.fields, availableTasks));
    const nextInternalComment = getHiredEquipmentInternalComment(form.fields);
    setInternalComment(nextInternalComment);
    savedFieldsRef.current = form.fields;
    savedDraftKeyRef.current = draftKey(parseHeGridRows(form.fields, availableTasks), nextInternalComment);
    historyBaselineRef.current = loggedEditKey(
      parseHeGridRows(form.fields, availableTasks),
      nextInternalComment,
    );
    setEditing(false);
  }, [form.id]);

  useEffect(() => {
    setHasResubmitted(false);
  }, [form.id, form.status]);

  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        let updated = row;
        if (updated.vendorId == null && updated.vendorName) {
          const vendor = lookups.vendors.find(
            (item) => item.name.trim().toLowerCase() === updated.vendorName!.trim().toLowerCase(),
          );
          if (vendor) {
            updated = { ...updated, vendorId: vendor.id };
            changed = true;
          }
        }
        if (updated.vendorId != null && updated.poId == null && updated.poNumber) {
          const po = lookups.purchaseOrders.find(
            (item) => item.poNumber === updated.poNumber && item.vendorId === updated.vendorId,
          );
          if (po) {
            updated = { ...updated, poId: po.id };
            changed = true;
          }
        }
        // A lump sum has no catalogue resource; its label is literally "Lump Sum",
        // so a vendor unit of that name would otherwise be matched onto it here.
        if (!updated.lumpSum && updated.resourceId == null && updated.resourceLabel) {
          const resource = lookups.resourcesForRow(updated).find((item) => item.label === updated.resourceLabel);
          if (resource) {
            updated = {
              ...updated,
              resourceId: resource.id,
              unit: resource.unit || updated.unit,
              vehicleType: resource.vehicleType ?? updated.vehicleType ?? null,
              fuelSurchargeEligible:
                updated.fuelSurchargeEligible ??
                defaultFuelSurchargeEligibleFromVehicleType(resource.vehicleType),
            };
            changed = true;
          }
        }
        return updated;
      });
      if (changed && loggedEditKey(prev, commentRef.current) === historyBaselineRef.current) {
        historyBaselineRef.current = loggedEditKey(next, commentRef.current);
      }
      if (changed && draftKey(prev, commentRef.current) === savedDraftKeyRef.current) {
        savedDraftKeyRef.current = draftKey(next, commentRef.current);
      }
      return changed ? next : prev;
    });
  }, [lookups.vendors, lookups.purchaseOrders, lookups.resourcesForRow]);

  // Leaving with unsaved changes asks first: closing or reloading the tab, or following a link.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const onLinkClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.getAttribute('target') === '_blank' || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      void confirmAction({
        title: 'Leave Without Saving?',
        body: 'Your changes to these lines have not been saved.',
        confirmLabel: 'Leave',
        destructive: true,
      }).then((leave) => {
        if (leave) window.location.assign((link as HTMLAnchorElement).href);
      });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onLinkClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onLinkClick, true);
    };
  }, [isDirty]);

  const markSaved = (fields: FormField[]) => {
    savedFieldsRef.current = fields;
    savedDraftKeyRef.current = draftKey(rows, internalComment);
    historyBaselineRef.current = loggedEditKey(rows, internalComment);
  };

  /** Save Changes: one History entry for the whole save (who, what changed, when). */
  const handleSaveChanges = async () => {
    const complete = rows.filter(
      (row) => row.date || row.taskId || row.qty != null || row.rate != null || row.resourceLabel,
    );
    if (!validateGridRows(complete).ok) {
      setSaveError('Fix the incomplete lines before saving.');
      return;
    }
    const persistRows = heGridSubmitRows(
      complete.filter((row) => row.taskId != null && row.qty != null && row.rate != null && row.date),
    );
    if (persistRows.length === 0) {
      setSaveError('Add at least one complete line before saving.');
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      const baseline = JSON.parse(historyBaselineRef.current) as {
        rows: ReturnType<typeof heGridSubmitRows>;
        internalComment: string;
      };
      const result = await updateHiredEquipmentLines(form.id, {
        rows: persistRows,
        internalComment,
        recordHistory: true,
        historyBaseline: baseline,
      });
      markSaved(result.fields);
      setSaveState('saved');
      setRows((prev) => withSavedLineComments(prev, result.fields, availableTasks));
      onFieldsReplace?.(form.id, result.fields);
      if (!waitingOnRevisions) setEditing(false);
    } catch (error: unknown) {
      setSaveState('idle');
      setSaveError(error instanceof Error ? error.message : 'Failed to save lines');
    }
  };

  /** Cancel / Discard Changes: back to the last saved lines, after asking if anything changed. */
  const handleDiscardChanges = async () => {
    if (isDirty) {
      const discard = await confirmAction({
        title: 'Discard Changes?',
        body: 'The lines go back to how they were last saved.',
        confirmLabel: 'Discard',
        destructive: true,
      });
      if (!discard) return;
    }
    setRows(parseHeGridRows(savedFieldsRef.current, availableTasks));
    setInternalComment(getHiredEquipmentInternalComment(savedFieldsRef.current));
    setSaveError(null);
    setSaveState('idle');
    if (!waitingOnRevisions) setEditing(false);
  };

  const handleAddLine = () => setRows((prev) => [...prev, emptyRowFromAnchor(anchor)]);
  const handleAddLumpSumLine = () => setRows((prev) => [...prev, lumpSumRowFromAnchor(anchor)]);

  const resolvedPoId = anchor.lockedPoId ?? null;
  const handleDuplicate = (clientId: string) => {
    setRows((prev) => {
      const source = prev.find((row) => row.clientId === clientId);
      if (!source) return prev;
      return [...prev, duplicateRow(source)];
    });
  };
  const handleRemove = (clientId: string) => setRows((prev) => prev.filter((row) => row.clientId !== clientId));
  const handleReAddPoResources = async () => {
    if (resolvedPoId == null) return;
    const resources = await hiredEquipmentPoService.getPoResources(resolvedPoId);
    setRows((prev) => appendMissingPoResources(anchor, prev, resources));
  };

  const handleResubmit = async (options?: { skipPoWarning?: boolean }) => {
    const complete = rows.filter(
      (row) => row.date || row.taskId || row.qty != null || row.rate != null || row.resourceLabel,
    );
    const validation = validateGridRows(complete);
    if (!validation.ok) {
      setSaveError('Fix incomplete lines before resubmitting.');
      return;
    }
    const persistRows = heGridSubmitRows(
      complete.filter((row) => row.taskId != null && row.qty != null && row.rate != null && row.date),
    );
    if (persistRows.length === 0) {
      setSaveError('Add at least one complete line before resubmitting.');
      return;
    }
    if (!options?.skipPoWarning) {
      const okToSubmit = await poBalanceWarning.check({
        rows: complete,
        lockedPoId: resolvedPoId,
        purchaseOrders: lookups.purchaseOrders,
        fuelSurcharges: lookups.fuelSurcharges,
      });
      if (!okToSubmit) return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      const baseline = JSON.parse(historyBaselineRef.current) as {
        rows: ReturnType<typeof heGridSubmitRows>;
        internalComment: string;
      };
      const result = await updateHiredEquipmentLines(form.id, {
        rows: persistRows,
        internalComment,
        resubmit: true,
        historyBaseline: baseline,
      });
      markSaved(result.fields);
      setSaveState('saved');
      setRows((prev) => withSavedLineComments(prev, result.fields, availableTasks));
      setHasResubmitted(true);
      onFieldsReplace?.(form.id, result.fields);
      await onResubmitted?.(form.id);
    } catch (error: unknown) {
      setSaveState('idle');
      setSaveError(error instanceof Error ? error.message : 'Failed to resubmit');
    }
  };

  const workArea = heWorkAreaLabels(form);

  return (
    <div>
      {waitingOnRevisions && (
        <Alert variant="warning" className="tw-mb-3">
          This submission was sent back and is waiting on you.{' '}
          {/* V2-060: one stop after the reviewer's comment, even when it ends with its own. */}
          {endSentence(`Fix the lines below${form.statusComments?.trim() ? ` – ${form.statusComments.trim()}` : ''}`)} Use{' '}
          <strong>Save Changes</strong> to keep your work, and{' '}
          <strong>Resubmit</strong> when you are done so it returns to the approval queue – not a new draft.
        </Alert>
      )}
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          {canEdit
            ? waitingOnRevisions
              ? 'Correct the lines that were sent back. Resubmit when the fix is complete.'
              : isEditing
                ? 'Correct quantities, add or remove lines. Each line is priced at the rate in effect on its date. Save Changes when you are done.'
                : 'Use Edit Lines to correct quantities or add or remove lines before approving.'
            : 'Line items for this submission.'}
        </p>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          {canEdit && saveState === 'saving' && (
            <span className="tw-text-xs tw-text-content-muted">Saving…</span>
          )}
          {canEdit && saveState === 'saved' && !saveError && !isDirty && (
            <span className="tw-text-xs tw-text-success">Saved</span>
          )}
          {isDirty && <span className="tw-text-xs tw-text-content-muted">Unsaved changes</span>}
          {canEdit && !isEditing && (
            <Button type="button" variant="outline-primary" size="sm" onClick={() => setEditing(true)}>
              Edit Lines
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => void handleDiscardChanges()}
                disabled={saveState === 'saving'}
              >
                {waitingOnRevisions ? 'Discard Changes' : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant={waitingOnRevisions ? 'outline-primary' : 'primary'}
                size="sm"
                onClick={() => void handleSaveChanges()}
                disabled={saveState === 'saving' || !isDirty}
              >
                Save Changes
              </Button>
            </>
          )}
          {waitingOnRevisions && canEdit && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void handleResubmit()}
              disabled={saveState === 'saving' || poBalanceWarning.checking}
            >
              {saveState === 'saving' ? (
                <>
                  <Spinner as="span" size="sm" className="tw-mr-1" />
                  Resubmitting…
                </>
              ) : poBalanceWarning.checking ? (
                'Checking…'
              ) : (
                'Resubmit'
              )}
            </Button>
          )}
        </div>
      </div>
      {saveError && (
        <Alert variant="danger" className="tw-mb-3">
          {saveError}
        </Alert>
      )}
      {(workArea.foremanArea || workArea.serviceArea) && (
        // C6a: the area of the work, as on the Submissions row.
        <p className="tw-mb-2 tw-text-sm tw-text-content-secondary" data-testid="he-work-area">
          {workArea.foremanArea && (
            <>
              Foreman Area <span className="tw-font-semibold tw-text-content">{workArea.foremanArea}</span>
            </>
          )}
          {workArea.foremanArea && workArea.serviceArea && <span className="tw-mx-2 tw-text-content-muted">·</span>}
          {workArea.serviceArea && (
            <>
              Service Area{' '}
              <span className="tw-font-semibold tw-text-content">{formatHeServiceAreaLabel(workArea.serviceArea)}</span>
            </>
          )}
        </p>
      )}
      <HiredEquipmentGrid
        anchor={anchor}
        rows={rows}
        onRowsChange={setRows}
        vendors={lookups.vendors}
        taskOptionForRow={lookups.taskOptionForRow}
        searchTasks={lookups.searchTasks}
        purchaseOrders={lookups.purchaseOrders}
        tasksByPoId={lookups.tasksByPoId}
        resourcesForRow={lookups.resourcesForRow}
        fuelSurcharges={lookups.fuelSurcharges}
        onAddLine={handleAddLine}
        onAddLumpSumLine={handleAddLumpSumLine}
        onDuplicateLine={handleDuplicate}
        onRemoveLine={handleRemove}
        onReAddPoResources={anchor.entryMethod === 'purchase_order' ? handleReAddPoResources : undefined}
        readOnly={!isEditing}
        showAttachmentColumn={false}
        sameDayHours={pricing.sameDayHours}
        canComment={isEditing}
      />
      <HiredEquipmentInternalComment
        value={internalComment}
        onChange={isEditing ? setInternalComment : undefined}
        className="tw-mt-3"
      />
      <HiredEquipmentTaskPictures formId={form.id} rows={rows} />
      <PoBalanceWarningModal
        show={poBalanceWarning.warnings != null}
        warnings={poBalanceWarning.warnings ?? []}
        confirmLabel="Resubmit anyway"
        onCancel={poBalanceWarning.dismiss}
        onConfirm={() => {
          poBalanceWarning.dismiss();
          void handleResubmit({ skipPoWarning: true });
        }}
      />
    </div>
  );
};

export default HiredEquipmentDetailedView;
